import * as T from 'three';

// The game airline's jet, built to look like a real airliner (as Lagos Life's does): a long round fuselage that
// tapers to a nose with cockpit windows and up into the tail, a row of cabin windows and doors, the airline's
// cheat line and name along both sides, swept wings with winglets, two engines under the wings, the tail fin in
// the airline's colour with its roundel, tailplanes, landing gear and navigation lights. Built at life size in
// metres (an A320 is about 38 m long): the map scales it down, the flight scene uses it as it is.
// The nose points along +x, up is +y, the right wing is +z.

export type Livery = {
  // body, tail fin and cheat line colours, the airline's name and the short code in the tail's roundel
  body: string;
  tail: string;
  stripe: string;
  name: string;
  code: string;
  // the jet's registration by the rear door (G-LLAB)
  registration?: string;
};
export type Jet = {
  group: T.Group;
  // the landing gear, folded away in the air
  setGear: (down: boolean) => void;
  // the spinning fans, the blinking lights
  update: (seconds: number) => void;
  length: number;
  dispose: () => void;
};

export const JET_LENGTH = 38;
// from the jet's middle down to the bottom of its tyres (stand it at this height on the ground)
export const JET_GROUND = 3.45;
const RADIUS = 2.05;
const NOSE = 6.2;
const TAIL = 11;

// the fuselage's radius along its length (0 the tail, 1 the nose), and how high its middle sits (the tail
// sweeps up to the fin)
const radiusAt = (t: number) => {
  const x = t * JET_LENGTH;
  if (x > JET_LENGTH - NOSE) {
    const k = (JET_LENGTH - x) / NOSE;
    return RADIUS * Math.sqrt(Math.max(0, 1 - (1 - k) * (1 - k)));
  }
  if (x < TAIL) {
    const k = x / TAIL;
    return 0.28 + (RADIUS - 0.28) * Math.pow(k, 0.75);
  }
  return RADIUS;
};
const liftAt = (t: number) => {
  const x = t * JET_LENGTH;
  if (x < TAIL) {
    const k = 1 - x / TAIL;
    return k * k * 1.15;
  }
  if (x > JET_LENGTH - NOSE) return -0.25 * Math.pow((x - (JET_LENGTH - NOSE)) / NOSE, 2);
  return 0;
};

// the fuselage's paint: cabin windows, doors, the cheat line, the airline's name both sides, the cockpit
// windows and the registration. Laid out with x along the length (tail to nose) and y round the body (the top
// in the middle of the picture), so the words come out the right way up on both sides.
function paintFuselage(livery: Livery, sharp: number) {
  // drawn at 2048 × 512 (sharp 1), or smaller for a jet seen from far away
  const width = 2048,
    height = 512;
  const canvas = document.createElement('canvas');
  canvas.width = width * sharp;
  canvas.height = height * sharp;
  const g = canvas.getContext('2d')!;
  g.scale(sharp, sharp);
  g.fillStyle = livery.body;
  g.fillRect(0, 0, width, height);
  // the belly, a little greyer
  const belly = g.createLinearGradient(0, 0, 0, height);
  belly.addColorStop(0, 'rgba(120,130,140,0.35)');
  belly.addColorStop(0.18, 'rgba(120,130,140,0)');
  belly.addColorStop(0.82, 'rgba(120,130,140,0)');
  belly.addColorStop(1, 'rgba(120,130,140,0.35)');
  g.fillStyle = belly;
  g.fillRect(0, 0, width, height);
  // y in the picture for a height on the body (-1 the belly, 1 the roof): the body is unrolled from the belly
  // (the picture's top and bottom edges), the right side in the top half, the roof across the middle, the left
  // side in the bottom half
  const row = (side: 1 | -1, up: number) => {
    const angle = Math.asin(Math.max(-1, Math.min(1, up))) / (Math.PI * 2);
    return (side === 1 ? 0.25 + angle : 0.75 - angle) * height;
  };
  const xAt = (metres: number) => (metres / JET_LENGTH) * width;
  for (const side of [1, -1] as const) {
    // the cheat line under the windows, in the airline's colours
    const below = row(side, -0.08);
    const lower = row(side, -0.3);
    g.fillStyle = livery.tail;
    g.fillRect(
      xAt(TAIL - 1),
      Math.min(below, lower),
      xAt(JET_LENGTH - NOSE + 1.5) - xAt(TAIL - 1),
      Math.abs(lower - below),
    );
    g.fillStyle = livery.stripe;
    const thin = row(side, -0.4);
    g.fillRect(
      xAt(TAIL - 1),
      Math.min(lower, thin),
      xAt(JET_LENGTH - NOSE + 1) - xAt(TAIL - 1),
      Math.abs(thin - lower),
    );
    // the cabin windows: one every 0.53 m, a gap at the wing exits
    const windowY = row(side, 0.18);
    g.fillStyle = '#1d2733';
    for (let m = TAIL + 1.2; m < JET_LENGTH - NOSE - 1.2; m += 0.53) {
      if (m > 15.8 && m < 17.2) continue;
      g.beginPath();
      g.roundRect(xAt(m) - 5, windowY - 7, 10, 14, 4);
      g.fill();
    }
    // the doors: front, rear and the overwing exits
    g.strokeStyle = 'rgba(40,52,64,0.55)';
    g.lineWidth = 2;
    for (const [m, w] of [
      [JET_LENGTH - NOSE - 0.6, 26],
      [TAIL + 0.6, 26],
      [16.2, 14],
      [16.9, 14],
    ] as const) {
      g.beginPath();
      g.roundRect(xAt(m) - w / 2, row(side, 0.46), w, row(side, -0.15) - row(side, 0.46), 5);
      g.stroke();
    }
    // the airline's name, above the windows, reading from the nose back on the left side (as painted on
    // real jets) and from the tail forward on the right
    g.save();
    const nameY = row(side, 0.44);
    g.translate(xAt((TAIL + JET_LENGTH - NOSE) / 2 + 0.6), nameY);
    if (side === 1) g.scale(1, -1);
    else g.scale(-1, 1);
    g.fillStyle = livery.tail;
    g.font = `900 46px "Arial Black", "Plus Jakarta Sans", Arial, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(livery.name.toUpperCase(), 0, 0, xAt(17));
    g.restore();
    // the registration, small, by the rear door
    if (livery.registration) {
      g.save();
      g.translate(xAt(TAIL + 2.4), row(side, 0.42));
      if (side === 1) g.scale(1, -1);
      else g.scale(-1, 1);
      g.fillStyle = '#3a4550';
      g.font = '700 18px Arial, sans-serif';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(livery.registration, 0, 0);
      g.restore();
    }
    // the cockpit windows
    g.fillStyle = '#16202b';
    const cockpit = row(side, 0.32);
    for (const [m, w] of [
      [JET_LENGTH - 1.95, 24],
      [JET_LENGTH - 2.55, 26],
      [JET_LENGTH - 3.2, 22],
    ] as const) {
      g.beginPath();
      g.moveTo(xAt(m) - w / 2, cockpit + 9);
      g.lineTo(xAt(m) + w / 2, cockpit + 9);
      g.lineTo(xAt(m) + w / 2 - 4, cockpit - 9);
      g.lineTo(xAt(m) - w / 2 + 2, cockpit - 9);
      g.closePath();
      g.fill();
    }
  }
  const texture = new T.CanvasTexture(canvas);
  texture.colorSpace = T.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

// the tail fin's roundel: the airline's short code in a circle of the stripe colour
function paintRoundel(livery: Livery) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const g = canvas.getContext('2d')!;
  g.fillStyle = livery.tail;
  g.fillRect(0, 0, 256, 256);
  g.fillStyle = livery.stripe;
  g.beginPath();
  g.arc(128, 128, 104, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = livery.tail;
  g.font = '900 108px "Arial Black", Arial, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(livery.code, 128, 136);
  const texture = new T.CanvasTexture(canvas);
  texture.colorSpace = T.SRGBColorSpace;
  return texture;
}

// a flat wing-like shape from its outline (seen from above, x along the jet, z out from the body), thickened
function slab(outline: [number, number][], thickness: number) {
  const shape = new T.Shape();
  outline.forEach(([x, z], i) => (i ? shape.lineTo(x, z) : shape.moveTo(x, z)));
  shape.closePath();
  const geometry = new T.ExtrudeGeometry(shape, {
    depth: thickness,
    bevelEnabled: true,
    bevelThickness: thickness * 0.35,
    bevelSize: thickness * 0.35,
    bevelSegments: 2,
  });
  // the shape is drawn in x/y: lay it flat (y → z) and centre its thickness
  geometry.rotateX(Math.PI / 2);
  geometry.translate(0, thickness / 2, 0);
  return geometry;
}

export function buildJet(livery: Livery, options: { detail?: 'high' | 'low' } = {}): Jet {
  const high = options.detail !== 'low';
  const group = new T.Group();
  const disposables: { dispose: () => void }[] = [];
  const keep = <D extends { dispose: () => void }>(item: D) => (disposables.push(item), item);
  const paint = (color: string, extra: Partial<T.MeshStandardMaterialParameters> = {}) =>
    keep(new T.MeshStandardMaterial({ color, roughness: 0.42, metalness: 0.12, ...extra }));
  const white = paint(livery.body);
  const tailPaint = paint(livery.tail);
  const grey = paint('#c3c9cf', { roughness: 0.5, metalness: 0.35 });
  const dark = paint('#2a3038', { roughness: 0.6 });
  const tyre = paint('#1c1f23', { roughness: 0.9 });
  const add = (geometry: T.BufferGeometry, material: T.Material, x = 0, y = 0, z = 0) => {
    keep(geometry);
    const mesh = new T.Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    group.add(mesh);
    return mesh;
  };

  // ---- the fuselage: a lathe along the length, its points evenly spaced so the paint lies straight
  const steps = high ? 90 : 36;
  const profile: T.Vector2[] = [];
  for (let i = 0; i <= steps; i += 1) profile.push(new T.Vector2(radiusAt(i / steps), (i / steps) * JET_LENGTH));
  // close the ends
  profile[0]!.x = 0.02;
  profile[steps]!.x = 0.001;
  // the seam along the belly, where no paint crosses it
  const body = new T.LatheGeometry(profile, high ? 40 : 18, Math.PI / 2);
  // the lathe's own u runs round the body and v along it: swap them, so the picture's x is the length
  const uv = body.getAttribute('uv') as T.BufferAttribute;
  for (let i = 0; i < uv.count; i += 1) uv.setXY(i, uv.getY(i), uv.getX(i));
  // along +x, the tail swept up
  body.rotateZ(-Math.PI / 2);
  const position = body.getAttribute('position') as T.BufferAttribute;
  for (let i = 0; i < position.count; i += 1) {
    const x = position.getX(i);
    position.setY(i, position.getY(i) + liftAt(x / JET_LENGTH));
  }
  body.computeVertexNormals();
  body.translate(-JET_LENGTH / 2, 0, 0);
  const skin = keep(paintFuselage(livery, high ? 1 : 0.5));
  add(body, keep(new T.MeshStandardMaterial({ map: skin, roughness: 0.38, metalness: 0.1 })));

  // ---- the wings: swept, with a little dihedral, winglets at the tips, flap track fairings underneath
  const wingRoot = -1.2;
  const wing = slab(
    [
      [3.1, 0],
      [-2.3, 0],
      [-7.3, 16.6],
      [-5.7, 16.9],
    ],
    0.32,
  );
  for (const side of [1, -1]) {
    const mesh = add(wing.clone(), white, wingRoot, -0.95, side * 1.4);
    mesh.scale.z = side;
    mesh.rotation.x = side * -0.08;
    // the winglet, standing up at the tip
    const winglet = add(
      slab(
        [
          [0.6, 0],
          [-0.9, 0],
          [-1.4, 1.8],
          [-0.6, 1.8],
        ],
        0.12,
      ),
      tailPaint,
    );
    winglet.rotation.x = -Math.PI / 2;
    winglet.position.set(wingRoot - 6.1, 0.45, side * 18.3);
    // the flap track fairings under the wing
    if (high)
      for (const out of [5.5, 9.5, 13]) {
        const fairing = add(new T.CapsuleGeometry(0.16, 1.6, 2, 6), grey);
        fairing.rotation.z = Math.PI / 2;
        fairing.position.set(wingRoot - 2.1 - out * 0.3, -1.22 + out * 0.08, side * (out + 1.4));
      }
  }
  // the wing box under the body
  add(new T.BoxGeometry(5.6, 0.8, 3.2), white, wingRoot, -1.55, 0);

  // ---- the engines: a nacelle with its intake ring, the fan, the exhaust cone, on a pylon under each wing
  const fans: T.Mesh[] = [];
  for (const side of [1, -1]) {
    const x = wingRoot + 2.2,
      y = -2.05,
      z = side * 5.9;
    const nacelle = add(new T.CylinderGeometry(1.0, 0.82, 4.3, high ? 28 : 12, 1, true), white, x, y, z);
    nacelle.rotation.z = Math.PI / 2;
    const lip = add(new T.TorusGeometry(0.98, 0.1, 8, high ? 28 : 12), grey, x + 2.15, y, z);
    lip.rotation.y = Math.PI / 2;
    const fan = add(new T.CircleGeometry(0.92, high ? 24 : 10), dark, x + 1.95, y, z);
    fan.rotation.y = Math.PI / 2;
    fans.push(fan);
    const spinner = add(new T.ConeGeometry(0.3, 0.6, 12), grey, x + 2.1, y, z);
    spinner.rotation.z = -Math.PI / 2;
    const exhaust = add(new T.ConeGeometry(0.55, 1.4, 16), dark, x - 2.6, y, z);
    exhaust.rotation.z = Math.PI / 2;
    add(new T.BoxGeometry(2.8, 0.9, 0.3), white, x + 0.2, y + 0.9, z);
    // a stripe of the airline's colour round the nacelle
    const band = add(new T.CylinderGeometry(1.005, 0.99, 0.5, high ? 28 : 12, 1, true), tailPaint, x + 0.8, y, z);
    band.rotation.z = Math.PI / 2;
  }

  // ---- the tail: the fin in the airline's colour with its roundel both sides, the tailplanes
  const fin = slab(
    [
      [0, 0],
      [-6.6, 0],
      [-9.4, 7.6],
      [-6.8, 7.6],
    ],
    0.3,
  );
  const finMesh = add(fin, tailPaint, -JET_LENGTH / 2 + 7.6, 1.55, 0);
  finMesh.rotation.x = -Math.PI / 2;
  const roundel = keep(paintRoundel(livery));
  const roundelMaterial = keep(new T.MeshStandardMaterial({ map: roundel, roughness: 0.5 }));
  for (const side of [1, -1]) {
    const badge = add(new T.CircleGeometry(1.45, 32), roundelMaterial, -JET_LENGTH / 2 + 2.1, 5.6, side * 0.27);
    badge.rotation.y = side === 1 ? 0 : Math.PI;
  }
  const tailplane = slab(
    [
      [0, 0],
      [-3.2, 0],
      [-4.9, 6.1],
      [-3.7, 6.2],
    ],
    0.22,
  );
  for (const side of [1, -1]) {
    const mesh = add(tailplane.clone(), white, -JET_LENGTH / 2 + 5.1, 1.0, side * 0.6);
    mesh.scale.z = side;
    mesh.rotation.x = side * -0.07;
  }

  // ---- the landing gear: the nose wheel and the two main legs
  const gear = new T.Group();
  group.add(gear);
  const leg = (x: number, z: number, length: number, wheels: number) => {
    const strut = new T.Mesh(keep(new T.CylinderGeometry(0.13, 0.13, length, 8)), grey);
    strut.position.set(x, -1.9 - length / 2 + 0.4, z);
    gear.add(strut);
    for (let i = 0; i < wheels; i += 1) {
      const wheel = new T.Mesh(keep(new T.CylinderGeometry(0.55, 0.55, 0.42, 16)), tyre);
      wheel.rotation.x = Math.PI / 2;
      wheel.position.set(x + (wheels > 2 ? (i < 2 ? 0.6 : -0.6) : 0), -1.5 - length, z + (i % 2 ? 0.3 : -0.3));
      gear.add(wheel);
    }
  };
  leg(JET_LENGTH / 2 - 5.2, 0, 1.25, 2);
  leg(wingRoot - 1.2, 3.6, 1.4, 4);
  leg(wingRoot - 1.2, -3.6, 1.4, 4);

  // ---- the lights: red on the left wingtip, green on the right, white strobes, the red beacon
  const glow = (color: string) => keep(new T.MeshBasicMaterial({ color }));
  const lightGeometry = keep(new T.SphereGeometry(0.18, 8, 6));
  const light = (color: string, x: number, y: number, z: number) => {
    const mesh = new T.Mesh(lightGeometry, glow(color));
    mesh.position.set(x, y, z);
    group.add(mesh);
    return mesh;
  };
  light('#3cff6a', wingRoot - 6.6, -0.3, 17.9);
  light('#ff3b3b', wingRoot - 6.6, -0.3, -17.9);
  const strobes = [light('#ffffff', wingRoot - 6.9, -0.3, 18.05), light('#ffffff', wingRoot - 6.9, -0.3, -18.05)];
  const beacon = light('#ff2a2a', 2, RADIUS + 0.05, 0);

  group.traverse((object) => {
    if (object instanceof T.Mesh) object.receiveShadow = false;
  });

  return {
    group,
    length: JET_LENGTH,
    setGear: (down) => {
      gear.visible = down;
    },
    update: (seconds) => {
      for (const fan of fans) fan.rotation.x = seconds * 30;
      const flash = seconds % 1.2 < 0.08;
      for (const strobe of strobes) strobe.visible = flash;
      beacon.visible = seconds % 1 < 0.5;
    },
    dispose: () => {
      for (const item of disposables) item.dispose();
    },
  };
}
